import { useTranslation } from '@dukanti/shared';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * F-022: Loading skeleton for the order detail page.
 *
 * Renders placeholder sections for header, customer, address, line items
 * (3 rows), and the actions sidebar.
 */
export function OrderDetailSkeleton(): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div
      data-testid="order-detail-skeleton"
      aria-label={t('admin.orders.detail.loading')}
      className="mx-auto max-w-screen-xl space-y-6 px-6 py-8"
    >
      {/* Header skeleton */}
      <div className="flex items-center gap-3">
        <Skeleton className="h-8 w-8 rounded-full" />
        <Skeleton className="h-6 w-40 rounded" />
        <Skeleton className="ms-auto h-6 w-24 rounded" />
      </div>

      {/* Two-column skeleton */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        {/* Main content */}
        <div className="flex flex-col gap-6">
          {/* Customer section */}
          <div className="border-border bg-card shadow-admin-card rounded-lg border p-6">
            <Skeleton className="mb-4 h-5 w-28 rounded" />
            <Skeleton className="mb-2 h-4 w-48 rounded" />
            <div className="flex gap-2">
              <Skeleton className="h-9 w-24 rounded" />
              <Skeleton className="h-9 w-24 rounded" />
            </div>
          </div>

          {/* Address section */}
          <div className="border-border bg-card shadow-admin-card rounded-lg border p-6">
            <Skeleton className="mb-4 h-5 w-32 rounded" />
            <Skeleton className="mb-2 h-4 w-56 rounded" />
            <Skeleton className="mb-2 h-4 w-40 rounded" />
            <Skeleton className="mb-4 h-4 w-32 rounded" />
            <Skeleton className="h-9 w-32 rounded" />
          </div>

          {/* Line items section */}
          <div className="border-border bg-card shadow-admin-card rounded-lg border p-6">
            <Skeleton className="mb-4 h-5 w-20 rounded" />
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="mb-4 flex items-center gap-4">
                <Skeleton className="h-14 w-14 rounded" />
                <div className="flex-1">
                  <Skeleton className="mb-2 h-4 w-40 rounded" />
                  <Skeleton className="h-3 w-24 rounded" />
                </div>
                <Skeleton className="h-4 w-16 rounded" />
              </div>
            ))}
          </div>
        </div>

        {/* Detail actions */}
        <div className="flex flex-col gap-6">
          <div className="border-border bg-card shadow-admin-card rounded-lg border p-6">
            <Skeleton className="mb-3 h-10 w-full rounded" />
            <Skeleton className="h-10 w-full rounded" />
          </div>
          <div className="border-border bg-card shadow-admin-card rounded-lg border p-6">
            <Skeleton className="mb-3 h-4 w-full rounded" />
            <Skeleton className="mb-3 h-4 w-4/5 rounded" />
            <Skeleton className="h-4 w-3/5 rounded" />
          </div>
        </div>
      </div>
    </div>
  );
}
